import React from 'react';
import { useModal } from 'react-modal-hook';
import { Field, FieldArray, FieldArrayRenderProps, FieldProps, Form, Formik } from 'formik';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import { AlertContent, ModalFactory } from 'components';
import { CalculatorParticipantType, IFormCalculatorRule, IFormDefaultPool, IFormValues, ShareRemainderType } from 'types';
import { SectionTitle } from '../../../../../Settings/components';
import { AddButton, StyledIconButton } from '../../../../styles';
import {
  ButtonContainer,
  Column,
  ErrorText,
  FieldContainer,
  JobTitleField,
  ModalContent,
  ModalInfo,
  StyledButton,
  StyledCheckbox,
  StyledDiv,
  TipCalculatorFieldsContainer,
  TipCalculatorRow,
  TipShareFormContainer,
} from '../../../shared/styles';

const MAX_DEFAULT_POOL_JOBS = 25;
const successMessage = 'Default Pool saved successfully';

interface ICreateDefaultPool {
  hideModal: () => void;
  activeRules?: IFormValues;
  onConfirm: (values: IFormValues, onSuccess?: () => void, successMessage?: string) => void;
  siteId: string;
  isLoading?: boolean;
  currentDefaultPool?: IFormCalculatorRule;
}

export const CreateDefaultPool = ({ hideModal, activeRules, onConfirm, siteId, isLoading, currentDefaultPool }: ICreateDefaultPool) => {
  const validationSchema = Yup.object().shape({
    includeNoLabor: Yup.boolean(),
    contributingJobs: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required('Please select a contributing job'),
        })
      )
      .when('includeNoLabor', {
        is: false,
        then: (schema) => schema.min(1, 'Error - You must Include tips without timecards and/or select a Contributing Job'),
      }),
    benefitingJobs: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Please select a receiving job'),
      })
    ),
  });

  const setInitialValues = (): IFormDefaultPool => {
    const id = uuid();
    const additionalContributingJobs = currentDefaultPool?.additionalContributors.map((contributor, index) => ({ id: index + 1 + id, ...contributor }));
    const benefitingJobs = currentDefaultPool?.sharePolicy?.remainder.beneficiaries.map((beneficiary, index) => ({ id: index + id, ...beneficiary.participants[0] }));
    return {
      includeNoLabor: currentDefaultPool?.includeNoLabor || false,
      contributingJobs: currentDefaultPool?.contributor ? [{ id, ...currentDefaultPool.contributor }, ...additionalContributingJobs] : [],
      benefitingJobs: benefitingJobs || [{ id, name: undefined, type: CalculatorParticipantType.JOB }],
    };
  };

  const handleSubmit = (values: IFormDefaultPool) => {
    const contributingJobs = values.contributingJobs.map((j) => j.name.trim().toLowerCase());
    const otherRules = activeRules.rules.filter((r) => !r.default && !contributingJobs.includes(r.contributor.name.trim().toLowerCase()));
    const [job, ...additionalJobs] = values.contributingJobs;

    const newDefaultPool: IFormCalculatorRule = {
      id: 'default-pool',
      additionalContributors: additionalJobs.map((j) => ({ name: j.name, type: j.type })),
      default: true,
      includeNoLabor: values.includeNoLabor,
      isVirtual: true,
      contributor: job, // This can be undefined if contributingJobs array is empty
      sharePolicy: {
        remainder: { type: ShareRemainderType.POOL, beneficiaries: values.benefitingJobs.map((j) => ({ participants: [{ name: j.name, type: j.type }] })) },
        shares: [],
      },
      value: 1,
    };

    const valuesToBeSubmitted: IFormValues = {
      ...activeRules,
      rules: [...otherRules, newDefaultPool],
    };

    onConfirm(valuesToBeSubmitted, hideModal, successMessage);
  };

  const [showCancelModal, hideCancelModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideCancelModal} isActive>
        <AlertContent
          title="Cancel changes to Default Pool?"
          message="Please note that your changes will not be saved."
          leftActionLabel="Go Back"
          rightActionLabel="Confirm"
          onClose={hideCancelModal}
          onLeftAction={hideCancelModal}
          onRightAction={hideModal}
          rightActionAppearance="destructive"
          maxWidth={450}
        />
      </ModalFactory>
    ),
    []
  );

  const handleCancel = (dirty: boolean) => {
    if (dirty) {
      showCancelModal();
    } else {
      hideModal();
    }
  };

  const onAddJob = (arrayHelpers: FieldArrayRenderProps, element: string) => {
    const id = uuid();
    arrayHelpers.push({ id, name: undefined, type: CalculatorParticipantType.JOB });

    requestAnimationFrame(() => {
      document.getElementById(`${element}-job-title-selector-${id}`).focus();
    });
  };

  const onRemoveJob = (jobsArrayHelpers: FieldArrayRenderProps, jobsIndex: number) => {
    jobsArrayHelpers.remove(jobsIndex);
  };

  const filterContributingJobs = (values: IFormDefaultPool, currentJobId: string) => {
    const jobs: string[] = [];

    // filter jobs from other rules already contributing or benefiting
    activeRules.rules.forEach((rule) => {
      if ((rule.sharePolicy || rule.isBenefitingFromShare) && !rule.default) {
        jobs.push(rule.contributor.name);
      }
    });

    // filter jobs already in current default pool
    values.contributingJobs.forEach((job) => {
      if (job.name && job.id !== currentJobId) {
        jobs.push(job.name);
      }
    });
    values.benefitingJobs.forEach((job) => {
      if (job.name) jobs.push(job.name);
    });
    return jobs;
  };

  const filterBenefitingJobs = (values: IFormDefaultPool, currentJobId: string) => {
    const jobs: string[] = [];

    // filter virtual contributors that cannot benefit from share policies
    activeRules.rules?.forEach((rule) => {
      if (rule.isVirtual && !rule.default) {
        jobs.push(rule.contributor.name);
      }
    });

    // filter jobs already in current default pool
    values.contributingJobs.forEach((job) => {
      if (job.name) jobs.push(job.name);
    });
    values.benefitingJobs.forEach((job) => {
      if (job.name && job.id !== currentJobId) {
        jobs.push(job.name);
      }
    });
    return jobs;
  };

  return (
    <ModalFactory title={`${currentDefaultPool ? 'Edit' : 'Create'} Default Pool`} onClose={showCancelModal} isActive maxWidth={600} closeOnOutSideClick={false} hideCloseButton>
      <ModalContent fullWidth>
        <ModalInfo size="small">These settings will affect the tips that fall outside of your normal rules.</ModalInfo>
        <br />
        <Formik initialValues={setInitialValues()} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ setFieldValue, values, dirty, setFieldTouched, errors, touched }) => (
            <Form>
              <TipShareFormContainer>
                <Field name="includeNoLabor">
                  {({ field }: FieldProps) => (
                    <StyledCheckbox
                      {...field}
                      onChange={() => setFieldValue('includeNoLabor', !values.includeNoLabor)}
                      data-cy="no-labor-checkbox"
                      label="Include tips without timecard entries"
                      size="small"
                      id="noLaborCheckbox"
                      checked={field.value}
                    />
                  )}
                </Field>
                <FieldArray
                  name="contributingJobs"
                  render={(contributingArrayHelpers) => (
                    <TipCalculatorFieldsContainer fullWidth>
                      <StyledDiv>
                        <SectionTitle>Contributing Jobs</SectionTitle>
                      </StyledDiv>
                      {values?.contributingJobs?.map((item, index) => (
                        <FieldContainer key={item.id}>
                          <Field name={`contributingJobs.${index}.name`}>
                            {({ field, meta }: FieldProps) => (
                              <Column>
                                <TipCalculatorRow>
                                  <JobTitleField
                                    {...field}
                                    defaultValue={field.value}
                                    placeholder="Select"
                                    createNewLabel="Add {search} as new job"
                                    siteId={siteId}
                                    onChange={(job) => {
                                      if (!meta.touched) setFieldTouched(`contributingJobs.${index}.name`);
                                      setFieldValue(`contributingJobs.${index}.name`, job?.value.title);
                                    }}
                                    filterOptions={filterContributingJobs(values, item.id)}
                                    $error={meta.error && meta.touched}
                                    loadOptions={false}
                                    dataCy="contributing-job-title-selector"
                                    selectId={`contributing-job-title-selector-${item.id}`}
                                    tooltipId={`contributing-job-${item.id}`}
                                  />
                                  <StyledIconButton label="Remove" onClick={() => onRemoveJob(contributingArrayHelpers, index)} appearance="white" size="small" icon="Close" data-cy="remove-contributing-job-button" $noMargin />
                                </TipCalculatorRow>
                                {meta.touched && meta.error && <ErrorText size="xxsmall">{meta.error}</ErrorText>}
                              </Column>
                            )}
                          </Field>
                        </FieldContainer>
                      ))}
                      {values.contributingJobs.length < MAX_DEFAULT_POOL_JOBS && (
                        <AddButton appearance="white" icon="Plus" label="Add Job" onClick={() => onAddJob(contributingArrayHelpers, 'contributing')} data-cy="add-contributing-job-button" />
                      )}
                    </TipCalculatorFieldsContainer>
                  )}
                />
                {touched.includeNoLabor && typeof errors.contributingJobs === 'string' && (
                  <ErrorText size="xxsmall" $margin={0} data-cy="default-pool-error">
                    {errors.contributingJobs}
                  </ErrorText>
                )}
                <FieldArray
                  name="benefitingJobs"
                  render={(benefitingArrayHelpers) => (
                    <TipCalculatorFieldsContainer fullWidth>
                      <StyledDiv>
                        <SectionTitle>Receiving Jobs</SectionTitle>
                      </StyledDiv>
                      {values?.benefitingJobs?.map((item, index) => (
                        <FieldContainer key={item.id}>
                          <Field name={`benefitingJobs.${index}.name`}>
                            {({ field, meta }: FieldProps) => (
                              <Column>
                                <TipCalculatorRow>
                                  <JobTitleField
                                    {...field}
                                    defaultValue={field.value}
                                    placeholder="Select"
                                    createNewLabel="Add {search} as new job"
                                    siteId={siteId}
                                    onChange={(job) => {
                                      if (!meta.touched) setFieldTouched(`benefitingJobs.${index}.name`);
                                      setFieldValue(`benefitingJobs.${index}.name`, job?.value.title);
                                    }}
                                    filterOptions={filterBenefitingJobs(values, item.id)}
                                    $error={meta.error && meta.touched}
                                    loadOptions={false}
                                    dataCy="benefiting-job-title-selector"
                                    selectId={`benefiting-job-title-selector-${item.id}`}
                                    tooltipId={`benefiting-job-${item.id}`}
                                  />
                                  {values.benefitingJobs.length > 1 && (
                                    <StyledIconButton label="Remove" onClick={() => onRemoveJob(benefitingArrayHelpers, index)} appearance="white" size="small" icon="Close" data-cy="remove-benefiting-job-button" $noMargin />
                                  )}
                                </TipCalculatorRow>
                                {meta.touched && meta.error && <ErrorText size="xxsmall">{meta.error}</ErrorText>}
                              </Column>
                            )}
                          </Field>
                        </FieldContainer>
                      ))}
                      {values.benefitingJobs.length < MAX_DEFAULT_POOL_JOBS && <AddButton appearance="white" icon="Plus" label="Add Job" onClick={() => onAddJob(benefitingArrayHelpers, 'benefiting')} data-cy="add-benefiting-job-button" />}
                    </TipCalculatorFieldsContainer>
                  )}
                />
              </TipShareFormContainer>

              <ButtonContainer isModal>
                <StyledButton label="Cancel" data-cy="cancel-button" appearance="white" onClick={() => handleCancel(dirty)} />
                <StyledButton type="submit" label="Save" data-cy="save-default-pool-button" isProcessing={isLoading} disabled={isLoading || !dirty} />
              </ButtonContainer>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ModalFactory>
  );
};
